import React, { useEffect, useMemo, useRef } from 'react';
import microApp from '@micro-zoe/micro-app';
import { useSelector } from 'react-redux';
import { RootState } from '@/redux/store';
import { buildURL } from '@/fetch/core/util';

const MAP_EDITOR_URL = process.env.JDX_APP_MAP_EDITOR;
const MAP_EDITOR_ASSET_BASE_URL =
  MAP_EDITOR_URL || (window as any).__MICRO_APP_PUBLIC_PATH__;
const MAP_EDITOR_PUBLIC_PATH = MAP_EDITOR_ASSET_BASE_URL
  ? /\/$/.test(MAP_EDITOR_ASSET_BASE_URL)
    ? MAP_EDITOR_ASSET_BASE_URL
    : `${MAP_EDITOR_ASSET_BASE_URL}/`
  : undefined;

const MAP_EDITOR_MICRO_GLOBAL_KEYS = [
  '__MAP_EDITOR_BOOTSTRAP_DATA__',
  '__MAP_EDITOR_ASSET_BASE_URL__',
  // 以下为旧 master 子应用兼容，待旧 master public-path.js 改造后可移除
  '__MICRO_APP_PUBLIC_PATH__',
  '__MICRO_APP_ENVIRONMENT__',
  'microApp',
] as const;

type MapEditorMicroGlobalKey = (typeof MAP_EDITOR_MICRO_GLOBAL_KEYS)[number];
type MapEditorMicroGlobalSnapshot = Record<
  MapEditorMicroGlobalKey,
  { existed: boolean; value: unknown }
>;

type MapEditorDataListener = (data: unknown) => void;

const mapEditorDataListeners = new Set<MapEditorDataListener>();
let latestMapEditorHostData: unknown = null;

const notifyMapEditorDataListeners = (data: unknown) => {
  latestMapEditorHostData = data;
  mapEditorDataListeners.forEach((callback) => callback(data));
};

const setMapEditorData = (data: Record<string, unknown>) => {
  microApp.setData('mapEditor', data);
  notifyMapEditorDataListeners(data);
};

const installMapEditorMicroGlobals = () => {
  const win = window as any;
  const snapshot = MAP_EDITOR_MICRO_GLOBAL_KEYS.reduce(
    (acc, key) => ({
      ...acc,
      [key]: {
        existed: Object.prototype.hasOwnProperty.call(win, key),
        value: win[key],
      },
    }),
    {} as MapEditorMicroGlobalSnapshot
  );

  if (MAP_EDITOR_PUBLIC_PATH) {
    // 长期协议：子应用 public-path.js 优先读 __MAP_EDITOR_BOOTSTRAP_DATA__.assetBaseUrl
    win.__MAP_EDITOR_BOOTSTRAP_DATA__ = {
      assetBaseUrl: MAP_EDITOR_PUBLIC_PATH,
    };
    win.__MAP_EDITOR_ASSET_BASE_URL__ = MAP_EDITOR_PUBLIC_PATH;

    // ---- 旧 master 子应用兼容（待旧 master public-path.js 改造后可整段移除）----
    win.__MICRO_APP_PUBLIC_PATH__ = MAP_EDITOR_PUBLIC_PATH;
    win.__MICRO_APP_ENVIRONMENT__ = true;
    win.microApp = {
      ...(typeof win.microApp === 'object' && win.microApp ? win.microApp : {}),
      dispatch: (data: Record<string, unknown>) => {
        document
          .querySelector('micro-app[name="mapEditor"]')
          ?.dispatchEvent(new CustomEvent('datachange', { detail: { data } }));
      },
      addDataListener: (
        callback: MapEditorDataListener,
        autoTrigger?: boolean
      ) => {
        if (typeof callback !== 'function') return;
        mapEditorDataListeners.add(callback);
        if (autoTrigger && latestMapEditorHostData) {
          callback(latestMapEditorHostData);
        }
      },
      removeDataListener: (callback: MapEditorDataListener) => {
        mapEditorDataListeners.delete(callback);
      },
      getData: () => latestMapEditorHostData ?? microApp.getData('mapEditor'),
    };
    // ---- 旧 master 兼容结束 ----
  }

  return () => {
    mapEditorDataListeners.clear();
    latestMapEditorHostData = null;
    MAP_EDITOR_MICRO_GLOBAL_KEYS.forEach((key) => {
      if (snapshot[key].existed) {
        win[key] = snapshot[key].value;
      } else {
        delete win[key];
      }
    });
  };
};

type MapEditorBootstrapData = {
  assetBaseUrl: string | undefined;
  useHostBaiduMapSdk: boolean;
  permissions: string[];
  mode: 'micro-app';
  // 旧 master 子应用 getPermissionById 直接读 login.appResourceInfoList
  login?: any;
};

/**
 * 腾讯地图 POI 搜索（供 master 版子应用降级使用）
 * master 版子应用通过 microApp.dispatch({ searchText }) 请求搜索，
 * 宿主用腾讯地图 API 查询后通过 microApp.setData({ mapOptions }) 回传结果。
 */
const qqMapSearch = async (searchText: string): Promise<void> => {
  const win = window as any;
  if (!win.$ || !win.qq) return;
  return new Promise((resolve) => {
    win.$.ajax({
      type: 'get',
      url: buildURL({
        absoluteURL: 'https://apis.map.qq.com/ws/place/v1/suggestion',
        urlParams: {
          key: 'V2LBZ-Y63CJ-YU7F3-X3WQW-7NFXH-RABW4',
          keyword: encodeURI(searchText),
          get_subpois: 1,
          output: 'jsonp',
        },
      }),
      dataType: 'jsonp',
      xhrFields: { mode: 'no-cors' },
      success: (res: any) => {
        if (res.status === 0) {
          const mapOptions = (res?.data ?? []).map(
            (item: any, index: number) => ({
              label: item.title + '-' + item.address,
              value: item.location.lng + ',' + item.location.lat + ':' + index,
            })
          );
          setMapEditorData({ mapOptions });
        }
        resolve();
      },
      error: () => resolve(),
    });
  });
};

const buildMapEditorBootstrapData = (login: any): MapEditorBootstrapData => {
  const appResourceInfoList = Array.isArray(login?.appResourceInfoList)
    ? login.appResourceInfoList
    : [];

  return {
    assetBaseUrl: MAP_EDITOR_PUBLIC_PATH,
    // 宿主已在 index.html 中加载 BMapGL SDK，子应用（百度版）可直接复用
    useHostBaiduMapSdk: typeof (window as any).BMapGL !== 'undefined',
    mode: 'micro-app',
    permissions: appResourceInfoList
      .map((item: any) => item?.resourceCode)
      .filter(
        (code: unknown): code is string => typeof code === 'string' && !!code
      ),
    // ---- 旧 master 兼容：原始 master getPermissionById 读 login.appResourceInfoList ----
    login,
  };
};

const MapEditor = () => {
  const login = useSelector((state: RootState) => state.login);
  const microAppRef = useRef<HTMLElement>(null);
  const restoreMicroGlobalsRef = useRef<(() => void) | null>(null);

  // 必须在 <micro-app> connectedCallback 拉取并执行子应用脚本前完成，
  // 否则旧 master 子应用入口里的 public-path.js 会先执行并错过这些变量。
  if (!restoreMicroGlobalsRef.current) {
    restoreMicroGlobalsRef.current = installMapEditorMicroGlobals();
  }

  const bootstrapData = useMemo(
    () => buildMapEditorBootstrapData(login),
    [login]
  );

  useEffect(() => {
    const win = window as any;
    // 长期协议：子应用 public-path.js 与 bridge.ts 优先读这两个全局
    win.__MAP_EDITOR_BOOTSTRAP_DATA__ = bootstrapData;
    if (MAP_EDITOR_PUBLIC_PATH) {
      win.__MAP_EDITOR_ASSET_BASE_URL__ = MAP_EDITOR_PUBLIC_PATH;
      // ---- 旧 master 兼容 ----
      win.__MICRO_APP_PUBLIC_PATH__ = MAP_EDITOR_PUBLIC_PATH;
      win.__MICRO_APP_ENVIRONMENT__ = true;
      // ---- 旧 master 兼容结束 ----
    }
    setMapEditorData(bootstrapData);

    return () => {
      // 子应用卸载时清理其通过 Portal 挂载到 body 上的元素
      // Popconfirm/Modal/Select/Cascader 等 antd 组件通过 Portal 渲染到 document.body
      // micro-app 不会自动清理这些外部 DOM，导致切换页面后高层级面板残留
      const commonModal = document.getElementById('commonModal');
      if (commonModal) {
        commonModal.remove();
      }
      document
        .querySelectorAll(
          [
            '.ant-popover',
            '.ant-tooltip',
            '.ant-select-dropdown',
            '.ant-cascader-menus',
            '.ant-picker-dropdown',
            '.ant-dropdown',
            '.ant-modal-root',
          ].join(', ')
        )
        .forEach((el) => {
          el.remove();
        });
    };
  }, [bootstrapData]);

  useEffect(() => {
    return () => {
      restoreMicroGlobalsRef.current?.();
      restoreMicroGlobalsRef.current = null;
    };
  }, []);

  // 兼容 master 版子应用（腾讯地图）：子应用通过 dispatch({ searchText }) 请求 POI 搜索，
  // 宿主用腾讯地图 API 查询后通过 setData({ mapOptions }) 回传。
  // 新版（百度地图）子应用不会发此事件，handler 不会被触发。
  useEffect(() => {
    const el = microAppRef.current;
    if (!el) return;
    const handler = async (e: any) => {
      const { searchText } = e?.detail?.data ?? {};
      if (searchText) {
        await qqMapSearch(searchText);
      }
    };
    el.addEventListener('datachange', handler);
    return () => el.removeEventListener('datachange', handler);
  }, []);

  return (
    <micro-app
      ref={microAppRef}
      name="mapEditor"
      url={MAP_EDITOR_URL}
      baseroute="/mapeditor"
      disableSandbox
    ></micro-app>
  );
};

export default React.memo(MapEditor);
